import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import {
    Button,
    TextField,
    Paper,
    Box,
    Typography,
    InputLabel,
    FormHelperText,
    LinearProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useParams } from "react-router-dom";
import HttpService from '../../services/httpService'
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { connect } from "react-redux";

const useStyles = makeStyles((theme) => ({
    fileInput: {
        display: 'none',
    },
    uploadButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        padding: '10px 20px',
        backgroundColor: '#4caf50',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#45a049',
        },
        '&.Mui-disabled': {
            backgroundColor: '#ccc',
            color: '#666',
        }
    },
    fileName: {
        marginTop: theme.spacing(1),
        marginLeft: theme.spacing(1),
        fontSize: "0.875rem",
        color: "#666",
        wordBreak: 'break-all'
    },
    uploadProgress: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    previewImage: {
        maxWidth: "100%",
        maxHeight: "200px",
        marginTop: theme.spacing(1),
        borderRadius: "4px",
        border: "1px solid #ddd"
    },
    uploadContainer: {
        border: '2px dashed #ccc',
        borderRadius: '8px',
        padding: theme.spacing(2),
        textAlign: 'center',
        backgroundColor: '#f9f9f9',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    fileInfo: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: '#f0f0f0',
        borderRadius: '4px'
    },
    removeFileButton: {
        marginLeft: theme.spacing(1),
        minWidth: 'auto',
        padding: '4px 8px',
        backgroundColor: '#f44336',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#d32f2f',
        }
    }
}));

const RefundBankDetailsCollect = forwardRef((props, ref) => {
    const classes = useStyles();
    const chequeInputRef = useRef(null);
    const params = useParams();
    const standalone = !props.data;

    const [formData, setFormData] = useState({
        email: "",
        accountHolder: "",
        bankName: "",
        branchName: "",
        accountNumber: "",
        confirmAccountNumber: "",
        ifscCode: "",
        uniqueLeadNumber: "",
        customerId: "",
        leadId: "",
        chequeDocumentLink: ""
    });

    const [errors, setErrors] = useState({});
    const [customerDetails, setCustomerDetails] = useState(null);
    const [chequeFile, setChequeFile] = useState(null);
    const [fileError, setFileError] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [uploadedFileLink, setUploadedFileLink] = useState("");
    const [previewUrl, setPreviewUrl] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [touchedFields, setTouchedFields] = useState({}); // Track which fields have been touched
    const [showConfirm, setShowConfirm] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(standalone);

    useEffect(() => {
        if (props.data) {
            const { customerId, uniqueLeadNumber, _id } = props.data;
            setFormData(prevData => ({
                ...prevData,
                customerId,
                uniqueLeadNumber: uniqueLeadNumber || "",
                leadId: _id || ""
            }));

            if (customerId?._id) {
                fetchCustomerDetails(customerId._id);
            }
        } else if (standalone && params.leadId) {
            fetchLeadDetails(params.leadId);
        }
    }, [props.data, params.leadId]);

    // Check  whether this customer has already submitted bank details for
    const checkAlreadySubmitted = async (customerId, leadId) => {
        try {
            const response = await HttpService.getBankDetailsStatus({ customerId, leadId, type: "refund" });
            if (response.data?.submitted) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error("Error checking bank details status:", error);
        } finally {
            setCheckingStatus(false);
        }
    };

    // Standalone (customer-facing) usage: load the lead by id from the URL
    const fetchLeadDetails = async (leadId) => {
        try {
            props.setLoading(true);
            const response = await HttpService.getLeadDetails(leadId);
            props.setLoading(false);

            const leadData = Array.isArray(response.data) ? response.data[0] : response.data;
            if (leadData && leadData.customerId) {
                setFormData(prevData => ({
                    ...prevData,
                    customerId: leadData.customerId,
                    uniqueLeadNumber: leadData.uniqueLeadNumber || leadData.lead_no || "",
                    leadId: leadData._id || leadId
                }));
                fetchCustomerDetails(leadData.customerId._id);
                checkAlreadySubmitted(leadData.customerId._id, leadData._id || leadId);
            } else {
                props.setSnackInfo("Failed to load lead details", "error");
                setCheckingStatus(false);
            }
        } catch (error) {
            props.setLoading(false);
            console.error("Error fetching lead details:", error);
            props.setSnackInfo("Error loading lead details", "error");
            setCheckingStatus(false);
        }
    };

    const fetchCustomerDetails = async (id) => {
        try {
            props.setLoading(true);
            const response = await HttpService.getcustomerDetailsForReferral(id);
            props.setLoading(false);

            if (response.data && response.data.data) {
                const customerData = response.data.data;
                setCustomerDetails(customerData);

                setFormData(prevData => ({
                    ...prevData,
                    email: customerData.email || "",
                    accountHolder: customerData.name || "",
                }));

                props.setSnackInfo("Customer details loaded successfully", "success");
            } else {
                props.setSnackInfo("Failed to load customer details", "error");
            }
        } catch (error) {
            props.setLoading(false);
            console.error("Error fetching customer details:", error);
            props.setSnackInfo("Error loading customer details", "error");
        }
    };

    const onChequeUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const extension = file.name.split('.').pop().toLowerCase();
        const allowedExtensions = ["pdf", "jpg", "jpeg", "png"];
        
        if (!allowedExtensions.includes(extension)) {
            setChequeFile(null);
            setUploadedFileLink("");
            setPreviewUrl("");
            setFileError("Invalid file type. Please upload a PDF, JPG, JPEG, or PNG file.");
            props.setSnackInfo("Invalid file type. Please upload a pdf, jpg, jpeg, or png file.", "warning");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setFileError("File size should be less than 5MB");
            props.setSnackInfo("File size should be less than 5MB", "warning");
            return;
        }

        const fd = new FormData();
        const fileName = `cheque_${formData.customerId || 'customer'}_${Date.now()}.${extension}`;
        fd.append('file', file, fileName);
        
        try {
            setIsUploading(true);
            props.setLoading(true);
            setFileError("");
            
            const fileUploadRes = await HttpService.uploadFile(fd);
            
            if (fileUploadRes.data) {
                const fileUrl = fileUploadRes.data;
                setUploadedFileLink(fileUrl);
                setPreviewUrl(fileUrl);
                setChequeFile(file);
                setFormData(prev => ({
                    ...prev,
                    chequeDocumentLink: fileUrl
                }));
                props.setSnackInfo("Cheque document uploaded successfully", "success");
            }
        } catch (err) {
            console.error("Error uploading cheque:", err);
            props.setSnackInfo(err?.response?.data ?? "Failed to upload cheque document", "error");
            setFileError("Failed to upload document");
            setUploadedFileLink("");
            setChequeFile(null);
        } finally {
            setIsUploading(false);
            props.setLoading(false);
        }
    };

    // Validate a single field
    const validateField = (name, value) => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        const nameRegex = /^[A-Za-z\s]+$/;
        
        switch (name) {
            case 'email':
                if (!emailRegex.test(value)) return "Enter a valid email address";
                return "";
            case 'accountHolder':
                if (!nameRegex.test(value)) return "Name should contain only letters and spaces";
                return "";
            case 'bankName':
                if (!nameRegex.test(value)) return "Bank name should contain only letters and spaces";
                return "";
            case 'branchName':
                if (!nameRegex.test(value)) return "Branch name should contain only letters and spaces";
                return "";
            case 'confirmAccountNumber':
                if (value !== formData.accountNumber) return "Account numbers do not match";
                return "";
            case 'accountNumber':
                if (!value) return "Account number is required";
                return "";
            case 'ifscCode':
                if (!value) return "IFSC code is required";
                return "";
            default:
                return "";
        }
    };

    // Validate all fields for errors
    const validateFormForErrors = () => {
        let tempErrors = {};
        
        tempErrors.email = validateField('email', formData.email);
        tempErrors.accountHolder = validateField('accountHolder', formData.accountHolder);
        tempErrors.bankName = validateField('bankName', formData.bankName);
        tempErrors.branchName = validateField('branchName', formData.branchName);
        tempErrors.accountNumber = validateField('accountNumber', formData.accountNumber);
        tempErrors.confirmAccountNumber = validateField('confirmAccountNumber', formData.confirmAccountNumber);
        tempErrors.ifscCode = validateField('ifscCode', formData.ifscCode);
        
        if (!formData.chequeDocumentLink) {
            tempErrors.chequeDocument = "Please upload a cheque document";
        }

        // Remove empty errors
        Object.keys(tempErrors).forEach(key => {
            if (!tempErrors[key]) delete tempErrors[key];
        });

        return tempErrors;
    };

    // Simple validation for button enable (no error messages)
    const isFormValid = () => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        const nameRegex = /^[A-Za-z\s]+$/;
        
        let isValid = true;
        
        if (!emailRegex.test(formData.email)) isValid = false;
        if (!nameRegex.test(formData.accountHolder)) isValid = false;
        if (!nameRegex.test(formData.bankName)) isValid = false;
        if (!nameRegex.test(formData.branchName)) isValid = false;
        if (formData.accountNumber !== formData.confirmAccountNumber) isValid = false;
        if (!formData.accountNumber) isValid = false;
        if (!formData.ifscCode) isValid = false;
        if (!formData.chequeDocumentLink) isValid = false;
        
        return isValid;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Mark field as touched
        setTouchedFields(prev => ({ ...prev, [name]: true }));
        
        // Validate this specific field and update error
        const fieldError = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: fieldError }));
        
        // Also validate confirmAccountNumber when accountNumber changes
        if (name === 'accountNumber' && formData.confirmAccountNumber) {
            const confirmError = validateField('confirmAccountNumber', formData.confirmAccountNumber);
            setErrors(prev => ({ ...prev, confirmAccountNumber: confirmError }));
        }
        
        // Notify parent of form validity for button enable/disable
        if (props.onFormChange) {
            props.onFormChange(isFormValid());
        }
    };

    const handleBlur = (fieldName) => {
        // Mark field as touched on blur
        setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
        
        // Validate the field on blur
        const fieldError = validateField(fieldName, formData[fieldName]);
        setErrors(prev => ({ ...prev, [fieldName]: fieldError }));
    };

    // Main function to submit bank details (called from parent)
    const submitBankDetails = async () => {
        if (isSubmitting) return { success: false, message: "Already submitting" };
        
        setIsSubmitting(true);
        
        // Mark all fields as touched to show validation errors
        const allFields = ['email', 'accountHolder', 'bankName', 'branchName', 'accountNumber', 'confirmAccountNumber', 'ifscCode'];
        const newTouched = {};
        allFields.forEach(field => { newTouched[field] = true; });
        setTouchedFields(newTouched);
        
        // Validate all and show errors
        const newErrors = validateFormForErrors();
        setErrors(newErrors);
        setShowValidationErrors(true);
        
        const hasErrors = Object.keys(newErrors).length > 0;
        
        if (hasErrors) {
            setIsSubmitting(false);
            return { 
                success: false, 
                message: "Please fill all required fields correctly",
                validationErrors: newErrors
            };
        }
        
        try {
            const dataToSubmit = {
                email: formData.email,
                accountHolder: formData.accountHolder,
                bankName: formData.bankName,
                branchName: formData.branchName,
                accountNumber: formData.accountNumber,
                confirmAccountNumber: formData.confirmAccountNumber,
                ifscCode: formData.ifscCode,
                uniqueLeadNumber: formData.uniqueLeadNumber,
                customerId: formData.customerId,
                leadId: formData.leadId,
                chequeDocumentLink: formData.chequeDocumentLink,
                type: "refund"
            };

            const response = await HttpService.refundBankDetailsSave(dataToSubmit);
            
            if (response.data && response.data.success === true) {
                setIsSubmitting(false);
                setErrors({});
                setShowValidationErrors(false);
                setTouchedFields({});
                return { 
                    success: true, 
                    message: response.data.message || "Bank details submitted successfully",
                    data: response.data
                };
            } else {
                setIsSubmitting(false);
                return { 
                    success: false, 
                    message: response.data.message || "Failed to submit bank details",
                    data: response.data
                };
            }
        } catch (error) {
            setIsSubmitting(false);
            console.error("Error submitting bank details:", error);
            
            if (error?.response) {
                const { status, data } = error.response;
                
                if (status === 403) {
                    return { 
                        success: false, 
                        message: data.message || "Enter Account and IFSC details correctly. Starting of IFSC should be in CAPITAL and Account number should be 9 digit",
                        statusCode: 403,
                        errors: data.errors
                    };
                } else if (status === 400) {
                    return { 
                        success: false, 
                        message: data.message || "Bank details already submitted",
                        statusCode: 400
                    };
                }
            }
            
            return { 
                success: false, 
                message: "Failed to connect to server. Please try again later.",
                statusCode: 500
            };
        }
    };

    // Get current form data
    const getFormData = () => {
        return formData;
    };

    // Standalone (customer-facing) submit: no parent ref driving this form.
    // Clicking Submit only opens a confirmation dialog; the API call happens in handleConfirmSubmit.
    const handleSubmitClick = () => {
        setShowConfirm(true);
    };

    const handleConfirmSubmit = async () => {
        setShowConfirm(false);
        const result = await submitBankDetails();
        if (result?.success) {
            setSubmitted(true);
            props.setSnackInfo("Bank details submitted successfully", "success");
        } else {
            props.setSnackInfo(result?.message || "Please fill all required fields correctly", "error");
        }
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
        submitBankDetails,
        isFormValid,
        validateForm: () => validateFormForErrors(),
        getFormData
    }));

    // Notify parent on form changes for button enable/disable
    useEffect(() => {
        if (props.onFormChange) {
            props.onFormChange(isFormValid());
        }
    }, [formData, uploadedFileLink]);

    const handleReset = () => {
        const { customerId, uniqueLeadNumber, leadId } = formData;
        setFormData({
            email: customerDetails?.email || "",
            accountHolder: customerDetails?.name || "",
            bankName: "",
            branchName: '',
            accountNumber: "",
            confirmAccountNumber: "",
            ifscCode: "",
            uniqueLeadNumber: uniqueLeadNumber || "",
            customerId,
            leadId: leadId || "",
            chequeDocumentLink: ""
        });
        setChequeFile(null);
        setFileError("");
        setUploadedFileLink("");
        setPreviewUrl("");
        setIsUploading(false);
        setErrors({});
        setShowValidationErrors(false);
        setTouchedFields({});
        if (chequeInputRef.current) {
            chequeInputRef.current.value = '';
        }
        if (props.onFormChange) {
            props.onFormChange(false);
        }
    };

    // Determine if a field should show error
    const shouldShowError = (fieldName) => {
        return (showValidationErrors || touchedFields[fieldName]) && !!errors[fieldName];
    };

    if (standalone && checkingStatus) {
        return (
            <Paper style={{ padding: "40px", maxWidth: "700px", margin: "auto", marginTop: "20px", marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">Loading...</Typography>
            </Paper>
        );
    }

    if (standalone && submitted) {
        return (
            <Paper style={{ padding: "40px", maxWidth: "700px", margin: "auto", marginTop: "20px", marginBottom: '20px', textAlign: 'center' }}>
                <Typography variant="h5" style={{ marginBottom: "10px" }}>Thank You</Typography>
                <Typography variant="body1">
                    Your bank details have been submitted successfully. We will get back to you once the refund is processed.
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper style={{ padding: "20px", maxWidth: "700px", margin: "auto", marginTop: "20px", marginBottom: '20px' }}>
            <Typography variant="h5" style={{ marginBottom: "10px" }}>Bank Details For Refund</Typography>
            <form>
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    InputProps={{ readOnly: true }}
                    required
                    margin="dense"
                    error={shouldShowError('email')}
                    helperText={shouldShowError('email') && errors.email}
                    style={{ backgroundColor: "#f0f0f0" }}
                />
                <TextField
                    fullWidth
                    label="Name of the Account Holder"
                    name="accountHolder"
                    value={formData.accountHolder}
                    onChange={handleChange}
                    onBlur={() => handleBlur('accountHolder')}
                    required
                    margin="dense"
                    error={shouldShowError('accountHolder')}
                    helperText={shouldShowError('accountHolder') && errors.accountHolder}
                />
                <TextField
                    fullWidth
                    label="Name of the Bank"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    onBlur={() => handleBlur('bankName')}
                    required
                    margin="dense"
                    error={shouldShowError('bankName')}
                    helperText={shouldShowError('bankName') && errors.bankName}
                />
                <TextField
                    fullWidth
                    label="Name of Branch"
                    name="branchName"
                    value={formData.branchName}
                    onChange={handleChange}
                    onBlur={() => handleBlur('branchName')}
                    required
                    margin="dense"
                    error={shouldShowError('branchName')}
                    helperText={shouldShowError('branchName') && errors.branchName}
                />
                <TextField
                    fullWidth
                    label="Account Number"
                    name="accountNumber"
                    type="password"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    onBlur={() => handleBlur('accountNumber')}
                    required
                    margin="dense"
                    error={shouldShowError('accountNumber')}
                    helperText={shouldShowError('accountNumber') && errors.accountNumber}
                />
                <TextField
                    fullWidth
                    label="Confirm Account Number"
                    name="confirmAccountNumber"
                    type="number"
                    value={formData.confirmAccountNumber}
                    onChange={handleChange}
                    onBlur={() => handleBlur('confirmAccountNumber')}
                    required
                    margin="dense"
                    error={shouldShowError('confirmAccountNumber')}
                    helperText={shouldShowError('confirmAccountNumber') && errors.confirmAccountNumber}
                />
                <TextField
                    fullWidth
                    label="IFSC Code"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleChange}
                    onBlur={() => handleBlur('ifscCode')}
                    required
                    margin="dense"
                    error={shouldShowError('ifscCode')}
                    helperText={shouldShowError('ifscCode') && errors.ifscCode}
                />
                <TextField
                    fullWidth
                    label="Your unique Lead Number or Project No"
                    name="uniqueLeadNumber"
                    value={formData.uniqueLeadNumber}
                    InputProps={{ readOnly: true }}
                    required
                    margin="dense"
                    style={{ backgroundColor: "#f0f0f0" }}
                />

                {/* Cheque Upload Field */}
                <Box mt={2} mb={2}>
                    <InputLabel style={{ marginBottom: "8px", fontWeight: 500 }}>
                        Upload Cheque Document *
                    </InputLabel>
                    
                    <div className={classes.uploadContainer}>
                        <input
                            ref={chequeInputRef}
                            id="cheque-upload-input"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={onChequeUpload}
                            className={classes.fileInput}
                            disabled={isUploading}
                        />
                        
                        <label htmlFor="cheque-upload-input">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                className={classes.uploadButton}
                                disabled={isUploading}
                                style={{
                                    backgroundColor: "#F47E46",
                                    color: "#FFF2EB",
                                    padding: "10px 24px",
                                    fontSize: "14px",
                                    textTransform: "none",
                                    marginBottom: "10px"
                                }}
                            >
                                {isUploading ? "Uploading..." : "Upload Cancel Cheque"}
                            </Button>
                        </label>
                        
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "5px" }}>
                            Click the button above to select a file
                        </Typography>
                        
                        {chequeFile && !uploadedFileLink && (
                            <div className={classes.fileInfo}>
                                <Typography variant="body2">
                                    📄 {chequeFile.name} ({(chequeFile.size / 1024).toFixed(2)} KB)
                                </Typography>
                                <Button
                                    size="small"
                                    className={classes.removeFileButton}
                                    onClick={() => {
                                        setChequeFile(null);
                                        setFileError("");
                                        if (chequeInputRef.current) {
                                            chequeInputRef.current.value = '';
                                        }
                                    }}
                                    disabled={isUploading}
                                >
                                    Remove
                                </Button>
                            </div>
                        )}
                        
                        {isUploading && (
                            <Box className={classes.uploadProgress}>
                                <LinearProgress />
                                <Typography variant="caption">Uploading document... Please wait</Typography>
                            </Box>
                        )}
                        
                        {uploadedFileLink && (
                            <Box mt={1}>
                                <div className={classes.fileInfo} style={{ backgroundColor: '#e8f5e9' }}>
                                    <Typography variant="body2" color="primary">
                                        ✓ Document uploaded successfully: {chequeFile?.name}
                                    </Typography>
                                    <Button
                                        size="small"
                                        className={classes.removeFileButton}
                                        onClick={() => {
                                            setUploadedFileLink("");
                                            setPreviewUrl("");
                                            setChequeFile(null);
                                            setFormData(prev => ({ ...prev, chequeDocumentLink: "" }));
                                            if (chequeInputRef.current) {
                                                chequeInputRef.current.value = '';
                                            }
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </div>
                                
                                {previewUrl && (previewUrl.match(/\.(jpg|jpeg|png)$/i)) && (
                                    <Box mt={1}>
                                        <img 
                                            src={previewUrl} 
                                            alt="Cheque preview" 
                                            className={classes.previewImage}
                                            style={{ maxWidth: '100%', maxHeight: '200px' }}
                                        />
                                        <br />
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => window.open(previewUrl, '_blank')}
                                            style={{ marginTop: 8 }}
                                        >
                                            View Full Size
                                        </Button>
                                    </Box>
                                )}
                                
                                {previewUrl && (previewUrl.match(/\.(pdf)$/i)) && (
                                    <Box mt={1}>
                                        <Button
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            onClick={() => window.open(previewUrl, '_blank')}
                                        >
                                            View Uploaded PDF
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                        
                        {(shouldShowError('chequeDocument') || (showValidationErrors && fileError)) && (
                            <FormHelperText error style={{ marginTop: "5px" }}>
                                {errors.chequeDocument || fileError}
                            </FormHelperText>
                        )}
                        
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: "10px" }}>
                            Accepted formats: PDF, JPG, JPEG, PNG (Max size: 5MB)
                        </Typography>
                    </div>
                </Box>

                <Box mt={2} display="flex" justifyContent={standalone ? "space-between" : "flex-end"}>
                    <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        onClick={handleReset}
                        disabled={isUploading}
                    >
                        Reset
                    </Button>
                    {standalone && (
                        <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            onClick={handleSubmitClick}
                            disabled={isUploading || isSubmitting}
                        >
                            Submit
                        </Button>
                    )}
                </Box>
            </form>
            {standalone && (
                <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                    <DialogTitle>Confirm Submission</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Please double check your bank details before submitting. Once submitted, do you want to proceed?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowConfirm(false)} color="secondary">
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmSubmit} color="primary" variant="contained" autoFocus>
                            Yes, Submit
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Paper>
    );
});

export default connect(null, { setLoading, setSnackInfo }, null, { forwardRef: true })(RefundBankDetailsCollect);